import { apiClient } from '../lib/apiClient';

/**
 * Lifecycle status of a ZeoVit dosing recommendation row
 * (mirrors `ZeovitDosingRecommendationStatus` in src/models/zeovitDosingRecommendation.ts).
 */
export type ZeovitRecommendationStatus = 'pending' | 'adopted' | 'superseded' | 'capped';

/**
 * Effect classification stored on a recommendation after a periodic check
 * (mirrors `ZeovitDosingRecommendationEffectClassification` in
 * src/models/zeovitDosingRecommendation.ts). `null` until a check has been run.
 */
export type ZeovitEffectClassification = 'darkening' | 'yellowing_paling' | 'browning' | 'no_change';

import type { ZeovitConcern } from '../constants/zeovitConcerns';

export type { ZeovitConcern } from '../constants/zeovitConcerns';

/**
 * Single row returned by GET /api/v1/aquariums/:aquariumId/zeovit-recommendations —
 * the fields the Iteration History list needs (one card per iteration).
 */
export interface ZeovitRecommendationListItem {
  readonly id: string;
  readonly aquariumId: string;
  readonly iterationNumber: number;
  readonly parentRecommendationId: string | null;
  readonly status: ZeovitRecommendationStatus;
  readonly effectClassification: ZeovitEffectClassification | null;
  /** ISO timestamp (JSON-serialised `Date`). */
  readonly createdAt: string;
  readonly adoptedAt: string | null;
}

function recommendationsPath(aquariumId: string): string {
  return `/api/v1/aquariums/${encodeURIComponent(aquariumId)}/zeovit-recommendations`;
}

function recommendationPath(aquariumId: string, recommendationId: string): string {
  return `${recommendationsPath(aquariumId)}/${encodeURIComponent(recommendationId)}`;
}

/**
 * Fetch all ZeoVit dosing recommendation iterations for the given aquarium.
 * The backend returns them newest first.
 */
export async function listZeovitRecommendations(
  aquariumId: string,
): Promise<ZeovitRecommendationListItem[]> {
  const response = await apiClient.get<{ data: ZeovitRecommendationListItem[] }>(
    recommendationsPath(aquariumId),
  );
  return response.data;
}

/**
 * Highest iteration number of a recommendation lineage before it is capped
 * (mirrors `MAX_ITERATIONS` in src/services/zeovitDosingRecommendationService.ts).
 */
export const ZEOVIT_MAX_ITERATIONS = 5;

/**
 * One product of a recommendation as persisted by the backend — values are
 * already clamped to the catalogue limits (mirrors `PersistedRecommendedProduct`
 * in src/services/zeovitDosingRecommendationService.ts).
 */
export interface ZeovitRecommendedProduct {
  readonly productId: string;
  readonly productName: string;
  /** Applied (clamped) dose. */
  readonly doseAmount: number;
  readonly doseUnit: string;
  /** Applied (clamped) interval between doses, in hours. */
  readonly frequencyHours: number;
  /** dosing_schedules frequency value derived from frequencyHours. */
  readonly frequency: string;
  /** Dose the AI suggested before clamping. */
  readonly aiSuggestedDoseAmount: number;
  /** Interval the AI suggested before clamping, in hours. */
  readonly aiSuggestedFrequencyHours: number;
  /** True when a value was reduced to the product's safety limits. */
  readonly clamped: boolean;
  /** Plain-language explanation for this product (AI output, may be absent). */
  readonly notes: string | null;
  /** Days between doses of the adopted plan (multi-day intervals). */
  readonly intervalDays?: number | null;
  /** Doses per day of the adopted plan (intervals below a day). */
  readonly timesPerDay?: number | null;
}

/** Verdict of the review of one current dosing plan. */
export type ZeovitDosingVerdict = 'keep' | 'adjust' | 'stop';

/** One current dosing plan as stored with the recommendation (dosingSnapshot.plans). */
export interface ZeovitCurrentDosingPlan {
  readonly planRef: string;
  readonly productName: string;
  readonly amount: number | null;
  readonly unit: string;
  readonly frequency: string;
  readonly intervalHours: number | null;
  readonly pump: boolean;
  readonly status: 'active' | 'paused' | 'ended' | 'scheduled';
  readonly catalogueProductId: string | null;
}

/** The current dosing a recommendation was based on (backend ZeovitDosingSnapshot). */
export interface ZeovitDosingSnapshot {
  readonly version: 1;
  readonly builtAt: string;
  readonly plans: readonly ZeovitCurrentDosingPlan[];
  readonly inactivePlans: readonly ZeovitCurrentDosingPlan[];
  readonly logs: readonly unknown[];
}

/** One entry of the review of the current dosing (backend DosingReviewEntry). */
export interface ZeovitDosingReviewEntry {
  readonly planRef: string;
  readonly productName: string;
  readonly amount: number | null;
  readonly unit: string;
  readonly catalogueProductId: string | null;
  readonly verdict: ZeovitDosingVerdict;
  readonly reason: string;
  readonly notAssessed?: boolean;
  readonly downgradedFrom?: 'adjust' | 'stop';
  readonly deferred?: boolean;
}

/** AI assessment of one reported concern ('other' = the free-text note). */
export interface ZeovitConcernAssessment {
  readonly concern: ZeovitConcern | 'other';
  readonly assessment: string;
  readonly actions: string;
  readonly notAssessed?: boolean;
}

/**
 * Full recommendation returned by
 * GET /api/v1/aquariums/:aquariumId/zeovit-recommendations/:recommendationId
 * (see docs/openapi/zeovit-dosing-recommendations.yaml, schema `Recommendation`).
 */
export interface ZeovitRecommendation extends ZeovitRecommendationListItem {
  readonly referenceImageKey: string | null;
  readonly measurementSnapshot: Record<string, number> | null;
  readonly icpSnapshot: Record<string, number> | null;
  /** Dates / units / stale flags of the snapshots; null (or absent) on older recommendations. */
  readonly snapshotMetadata?: ZeovitSnapshotMetadata | null;
  readonly recommendedProducts: ZeovitRecommendedProduct[];
  readonly rationale: string | null;
  readonly adoptedDosingScheduleId: string | null;
  /** v3 — absent / null on older recommendations. */
  readonly dosingSnapshot?: ZeovitDosingSnapshot | null;
  readonly dosingReview?: readonly ZeovitDosingReviewEntry[] | null;
  readonly concerns?: readonly ZeovitConcern[];
  readonly concernNote?: string | null;
  readonly concernAssessment?: readonly ZeovitConcernAssessment[] | null;
}

/** A plan adopting ends: 'stop' (review verdict) or 'replaced' by a recommended product. */
export interface ZeovitPlanToEnd {
  readonly planRef: string;
  readonly productName: string;
  readonly doseAmount: number | null;
  readonly doseUnit: string;
  readonly reason: 'stop' | 'replaced';
  /** The review's reason (stop verdicts). */
  readonly reviewReason?: string | null;
}

/** GET …/:recommendationId/adopt-preview. */
export interface ZeovitAdoptPreview {
  readonly plansToEnd: readonly ZeovitPlanToEnd[];
  readonly unchangedPlanRefs: readonly string[];
  readonly createCount: number;
}

/** Response of POST …/:recommendationId/adopt. */
export interface ZeovitAdoptResult {
  readonly recommendation: ZeovitRecommendation;
  readonly createdDosingScheduleIds: string[];
  /** Null when the recommendation only ended plans. */
  readonly adoptedDosingScheduleId: string | null;
  readonly endedPlans?: readonly ZeovitPlanToEnd[];
  readonly unchangedPlanRefs?: readonly string[];
}

/** Source of a stored value (mirrors `ZeovitMeasurementSource` in src/services/zeovitInputSnapshot.ts). */
export type ZeovitMeasurementSource = 'manual' | 'photo' | 'icp' | 'fauna_marin' | 'controller' | 'device';

/** One latest stored value (GET …/zeovit-recommendations/inputs). */
export interface ZeovitMeasurementInput {
  readonly parameterId: string;
  readonly parameterName: string;
  readonly value: number;
  readonly unit: string;
  /** ISO timestamp. */
  readonly measuredAt: string;
  /** 'YYYY-MM-DD' for an ICP value known only by its report date, else null. */
  readonly measuredDate: string | null;
  readonly source: ZeovitMeasurementSource;
  readonly ageDays: number;
  /** Older than `staleAfterDays`. */
  readonly stale: boolean;
}

/** The latest ICP analysis the server would use. */
export interface ZeovitIcpInput {
  readonly analysisId: string;
  /** 'YYYY-MM-DD'. */
  readonly reportDate: string;
  readonly provider: string | null;
  readonly ageDays: number;
  readonly parameterCount: number;
  readonly values: Readonly<Record<string, number>>;
  readonly units: Readonly<Record<string, string>>;
}

/**
 * The stored data a recommendation or check would use right now (mirrors `ZeovitInputs`
 * in src/services/zeovitInputSnapshot.ts). No AI call.
 */
export interface ZeovitInputs {
  readonly builtAt: string;
  readonly staleAfterDays: number;
  readonly measurements: readonly ZeovitMeasurementInput[];
  readonly latestMeasurementAt: string | null;
  readonly icp: ZeovitIcpInput | null;
  /** ZeoVit core parameters without any stored value. */
  readonly missingParameters: readonly string[];
  readonly staleParameters: readonly string[];
  /** A core parameter is missing or stale: ask the user once for a water test. */
  readonly needsUpdate: boolean;
  /** v3: current dosing included in every recommendation (counts). */
  readonly dosing?: { readonly activePlanCount: number; readonly inactivePlanCount: number; readonly logEntryCount: number };
  /** v3: the volume doses are calculated for; null → generation answers 409 AQUARIUM_VOLUME_REQUIRED. */
  readonly volumeLiters?: number | null;
}

/** Basis stored with a recommendation (mirrors `ZeovitSnapshotMetadata` of the backend). */
export interface ZeovitSnapshotMetadata {
  readonly version: 1;
  readonly builtAt: string;
  readonly staleAfterDays: number;
  readonly measurementOrigin: 'stored' | 'request' | 'none';
  readonly icpOrigin: 'stored' | 'request' | 'none';
  readonly measurements: Readonly<
    Record<string, { unit: string; measuredAt: string; source: ZeovitMeasurementSource; ageDays: number; stale: boolean }>
  >;
  readonly latestMeasurementAt: string | null;
  readonly icp: {
    readonly analysisId: string;
    readonly reportDate: string;
    readonly provider: string | null;
    readonly ageDays: number;
    readonly parameterCount: number;
    readonly units: Readonly<Record<string, string>>;
  } | null;
  readonly missingParameters: readonly string[];
  readonly staleParameters: readonly string[];
}

/** The latest stored measurements and ICP analysis the server would use (no AI call). */
export async function getZeovitInputs(aquariumId: string): Promise<ZeovitInputs> {
  const response = await apiClient.get<{ data: ZeovitInputs }>(`${recommendationsPath(aquariumId)}/inputs`);
  return response.data;
}

/** Fetch one ZeoVit dosing recommendation of the given aquarium. */
export async function getZeovitRecommendation(
  aquariumId: string,
  recommendationId: string,
): Promise<ZeovitRecommendation> {
  const response = await apiClient.get<{ data: ZeovitRecommendation }>(
    recommendationPath(aquariumId, recommendationId),
  );
  return response.data;
}

/**
 * Adopt a pending recommendation into the aquarium's dosing schedules. The backend
 * answers 409 (`RECOMMENDATION_NOT_PENDING` with `details.status`, or
 * `DOSING_SCHEDULE_CONFLICT`) without writing anything; that surfaces as an ApiError.
 */
export async function adoptZeovitRecommendation(
  aquariumId: string,
  recommendationId: string,
  confirmedEndPlanRefs: readonly string[] = [],
): Promise<ZeovitAdoptResult> {
  const response = await apiClient.post<{ data: ZeovitAdoptResult }>(
    `${recommendationPath(aquariumId, recommendationId)}/adopt`,
    { confirmedEndPlanRefs },
  );
  return response.data;
}

/** What adopting would change right now (the plans it ends) — no writes. */
export async function getZeovitAdoptPreview(aquariumId: string, recommendationId: string): Promise<ZeovitAdoptPreview> {
  const response = await apiClient.get<{ data: ZeovitAdoptPreview }>(
    `${recommendationPath(aquariumId, recommendationId)}/adopt-preview`,
  );
  return response.data;
}

/**
 * Delete the whole history (all iterations of the lineage) containing the
 * recommendation. Adopted dosing plans stay unchanged. 204.
 */
export async function deleteZeovitLineage(aquariumId: string, recommendationId: string): Promise<void> {
  await apiClient.delete(`${recommendationPath(aquariumId, recommendationId)}/lineage`);
}



/**
 * Measurement or ICP values (optional request overrides — the app itself no longer
 * sends them; the server uses the aquarium's stored data): parameter name
 * (letter first, then letters, digits or `_`) → finite number between 0 and 1,000,000.
 */
export type ZeovitValueSnapshot = Readonly<Record<string, number>>;

/**
 * A generated recommendation (mirrors `ZeovitRecommendationResult` in
 * src/services/zeovitDosingRecommendationService.ts) — the 201 `data` of POST
 * …/zeovit-recommendations and the `nextIteration` of a periodic check.
 */
export interface ZeovitRecommendationResult {
  readonly recommendation: ZeovitRecommendation;
  /** True when any recommended product was clamped to its catalogue limits. */
  readonly clamped: boolean;
  /** Manual-review message when the lineage is capped. */
  readonly message: string | null;
}

/** Body of POST /api/v1/aquariums/:aquariumId/zeovit-recommendations. */
export interface CreateZeovitRecommendationInput {
  /** Object key of a photo uploaded for this aquarium (`aquariums/<aquariumId>/…`). */
  readonly referenceImageKey: string;
  readonly measurementSnapshot?: ZeovitValueSnapshot | null;
  readonly icpSnapshot?: ZeovitValueSnapshot | null;
  /** v3: user-reported concerns (optional). */
  readonly concerns?: readonly ZeovitConcern[];
  readonly concernNote?: string;
}

/**
 * Generate a first ZeoVit dosing recommendation (one metered AI call) from a reference
 * photo; the server adds the aquarium's latest stored measurements and ICP analysis.
 */
export async function createZeovitRecommendation(
  aquariumId: string,
  input: CreateZeovitRecommendationInput,
): Promise<ZeovitRecommendationResult> {
  const response = await apiClient.post<{ data: ZeovitRecommendationResult }>(
    recommendationsPath(aquariumId),
    input,
  );
  return response.data;
}

/**
 * Body of POST …/zeovit-recommendations/:recommendationId/check (validated by a strict
 * Zod schema in src/controllers/zeovitDosingRecommendation.controller.ts).
 */
export interface ZeovitCheckRequest {
  /** Object key of the new photo; must lie under `aquariums/<aquariumId>/`. */
  readonly newPhotoKey: string;
  /** The new measurements (sent as `measurementSnapshot`, as the backend expects). */
  readonly measurementSnapshot?: ZeovitValueSnapshot | null;
  /** v3: new concerns; omitted → the lineage's concerns carry over. */
  readonly concerns?: readonly ZeovitConcern[];
  readonly concernNote?: string;
}

/** Response of the periodic check (`PeriodicCheckResult` of the backend service). */
export interface ZeovitCheckResult {
  /** The checked recommendation, now carrying its effect classification. */
  readonly recommendation: ZeovitRecommendation;
  /** The next iteration (pending, or capped) — null when the effect was an improvement. */
  readonly nextIteration: ZeovitRecommendationResult | null;
}

/** Error code of the backend's per-aquarium check rate limit (HTTP 429). */
export const CHECK_RATE_LIMITED_CODE = 'CHECK_RATE_LIMITED';

/** Run a periodic check: compares the new photo + measurements against the reference. */
export async function runZeovitCheck(
  aquariumId: string,
  recommendationId: string,
  request: ZeovitCheckRequest,
): Promise<ZeovitCheckResult> {
  const response = await apiClient.post<{ data: ZeovitCheckResult }>(
    `${recommendationPath(aquariumId, recommendationId)}/check`,
    request,
  );
  return response.data;
}
